import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface Notification {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationContextType {
  showNotification: (message: string, severity?: AlertColor, options?: {
    duration?: number;
    action?: { label: string; onClick: () => void };
  }) => void;
  showSuccess: (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => void;
  showError: (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => void;
  showWarning: (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => void;
  showInfo: (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => void;
  hideNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((
    message: string,
    severity: AlertColor = 'info',
    options: {
      duration?: number;
      action?: { label: string; onClick: () => void };
    } = {}
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    const notification: Notification = {
      id,
      message,
      severity,
      duration: options.duration ?? (severity === 'error' ? 0 : 6000), // Errors don't auto-dismiss
      action: options.action,
    };

    setNotifications(prev => [...prev, notification]);
  }, []);

  const showSuccess = useCallback((message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
    showNotification(message, 'success', options);
  }, [showNotification]);

  const showError = useCallback((message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
    showNotification(message, 'error', options);
  }, [showNotification]);

  const showWarning = useCallback((message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
    showNotification(message, 'warning', options);
  }, [showNotification]);

  const showInfo = useCallback((message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
    showNotification(message, 'info', options);
  }, [showNotification]);

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleClose = useCallback((id: string) => {
    hideNotification(id);
  }, [hideNotification]);

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideNotification,
        clearAllNotifications,
      }}
    >
      {children}
      
      {/* Render all notifications */}
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration}
          onClose={() => handleClose(notification.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ mb: 1 }}
        >
          <Alert
            onClose={() => handleClose(notification.id)}
            severity={notification.severity}
            variant="filled"
            action={notification.action ? (
              <button
                onClick={notification.action.onClick}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                {notification.action.label}
              </button>
            ) : undefined}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}


